import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createTheme, ThemeProvider } from "@mui/material"
import { LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { shallow } from "zustand/shallow"
import { useTranslation } from "react-i18next"

import AppBar from "@/app/components/appBar/AppBar"
import Snackbar from "@/components/snackbar/Snackbar"
import BreadCrumbs from "@/components/breadcrumbs/BreadCrumbs"
import LatestNoticeDialog from "@/app/components/latestNoticeDialog/LatestNoticeDialog"
import { createDrawerStore, createGlobalStore } from "@/stores/global"
import { createAccountStore } from "@/stores/account"
import SideDrawer from "@/app/components/sidenavDrawer/SideNavDrawer"
import { useLatestNotificationQuery } from "@/hooks/api/useNoticeQuery"
import { Notice } from "@/types/notice"
import { APP_ROUTES } from "@/constants/routes"
import { useAuth } from "@/providers/AuthProvider"
import { getTokenPayload } from "@/utils/token"

import useRouter from "@/hooks/router/useRoute"
import { Route } from "@/types/route"
import { useLocation } from "@tanstack/react-router"

import classNames from "classnames"
import { colors } from "@/components/colors"
import Footer from "./components/footer/Footer"
import IconButton from "@/components/button/IconButton.tsx"

const MOBILE_BREAKPOINT = 1200

// auth 체크 함수: auth가 없으면 전부 접근 가능, 있으면 명시된 type만 접근 가능
const hasRouteAuth = (route: Route, accountType?: string): boolean => {
  if (!route.auth) return true
  if (!accountType) return false
  return route.auth.includes(accountType as "P" | "C")
}

// 현재 경로에 해당하는 라우트 체인을 재귀적으로 찾기 (부모 -> 자식 순서)
const findRouteChainByPath = (
  routes: Route[],
  path: string,
  chain: Route[] = [],
): Route[] | null => {
  for (const route of routes) {
    // 정확히 일치하거나 하위 경로인 경우
    if (path === route.path || (path.startsWith(route.path + "/") && route.path !== "/")) {
      const newChain = [...chain, route]
      // 자식 라우트가 있으면 더 구체적인 매칭 시도
      if (route.children) {
        const childChain = findRouteChainByPath(route.children, path, newChain)
        if (childChain) return childChain
      }
      return newChain
    }
  }
  return null
}

// 라우트 체인 전체의 auth를 체크 (부모 중 하나라도 권한이 없으면 false)
const hasRouteChainAuth = (chain: Route[], accountType?: string): boolean => {
  return chain.every((route) => hasRouteAuth(route, accountType))
}

// 앱바, 사이드 네비게이션 컴포넌트, breadcrumb 등을 포함하는 컨테이너 컴포넌트
export default function Container({
  children,
}: Readonly<{
  children?: React.ReactNode
}>) {
  const { i18n } = useTranslation()
  const [isDrawerOpen, setIsDrawerOpen] = useState(true)
  const [isMobile, setIsMobile] = useState(false)
  const [noticeDialogOpen, setNoticeDialogOpen] = useState(false)
  const [noticeData, setNoticeData] = useState<Notice | null>(null)
  const hasShownNoticeRef = useRef(false)

  const { data: latestNotice } = useLatestNotificationQuery(i18n.language)
  const { token } = useAuth()

  // 최근 공지가 있고, 읽지 않았으면 다이얼로그 띄우기 (admin 그룹 제외)
  useEffect(() => {
    if (!latestNotice || latestNotice.beRead || hasShownNoticeRef.current) return

    // admin 그룹은 공지 다이얼로그 표시 안 함
    if (token) {
      try {
        const payload = getTokenPayload(token)
        if (payload.groups?.includes("admin")) return
      } catch {
        // 토큰 파싱 실패 시 그냥 진행
      }
    }

    hasShownNoticeRef.current = true
    setNoticeData(latestNotice)
    setNoticeDialogOpen(true)
  }, [latestNotice, token])

  const { snackBar, setSnackBar } = createGlobalStore(
    (store) => ({
      snackBar: store.snackBar,
      setSnackBar: store.setSnackBar,
    }),
    shallow,
  )
  const { setIsOpen, setIsMobile: setDrawerIsMobile } = createDrawerStore()

  useEffect(() => {
    setIsOpen(isDrawerOpen)
  }, [isDrawerOpen])

  useEffect(() => {
    setDrawerIsMobile(isMobile)
  }, [isMobile])

  const muiTheme = createTheme({
    typography: {
      fontFamily: "'pretendard', 'Noto Sans SC', 'Noto Sans KR', sans-serif",
    },
    palette: {
      primary: {
        main: colors.primary[600],
      },
    },
  })

  const location = useLocation()
  const pathName = location.pathname
  const router = useRouter()
  const { info } = createAccountStore()

  // 권한 없는 라우트 접근 시 홈으로 리다이렉트 (부모 라우트의 auth도 체크)
  useEffect(() => {
    // info가 아직 로딩 중이면 체크하지 않음
    if (!info) return

    const currentPath = pathName.split("?")[0]
    const routeChain = findRouteChainByPath(APP_ROUTES, currentPath)

    if (routeChain && !hasRouteChainAuth(routeChain, info.type)) {
      // 권한 없는 라우트에 접근 시도 시 홈으로 리다이렉트
      router?.push("/home")
    }
  }, [pathName, info, router])

  const routeParser = (route: Route): Route => {
    if (route.children) {
      return route.children?.map((child) => {
        return routeParser(child)
      })[0]
    }

    return route
  }

  const onPopstate = () => {
    // 브라우저 뒤로가기, 앞으로가기 버튼을 눌렀을 때, 라우터의 이벤트를 호출하여 라우터의 상태를 업데이트
    router?.callChangeCompleteEvent(pathName)
  }

  const onResize = () => {
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
  }

  useEffect(() => {
    if (isMobile) {
      setIsDrawerOpen(false)
    } else {
      setIsDrawerOpen(true)
    }
  }, [isMobile])

  useEffect(() => {
    // 초기 화면 크기 체크
    onResize()
    // 윈도우 리사이즈 이벤트 핸들러 등록
    window.addEventListener("resize", onResize)

    return () => {
      // 컴포넌트 언마운트 시 이벤트 핸들러 제거
      window.removeEventListener("resize", onResize)
    }
  }, [])

  const handleSnackbarClose = useCallback(() => {
    const currentSnackBar = createGlobalStore.getState().snackBar
    setSnackBar({ ...currentSnackBar, open: false })
  }, [setSnackBar])

  const isHome = useMemo(() => {
    const normalizedPath = location.pathname.replace(/\/$/, "") // trailing slash 제거
    return normalizedPath === "/home"
  }, [location.pathname])

  const isLegacyReport = useMemo(() => {
    const normalizedPath = location.pathname.replace(/\/$/, "") // trailing slash 제거
    return (
      normalizedPath === "/report/report" ||
      normalizedPath === "/settings/report/report" ||
      normalizedPath === "/dashboards/periodization"
    )
  }, [location.pathname])

  const isExternalPage = useMemo(() => {
    return (
      location.pathname.startsWith("/signup") ||
      (location.pathname.startsWith("/export") && !location.pathname.startsWith("/export/csv"))
    )
  }, [location.pathname])

  // 웹뷰 모드: URL에 ?mode=webview가 있을 때만 활성화 (네비게이션 UI 숨김 + 페이지 이동 차단)
  const isWebView = useMemo(() => {
    return new URLSearchParams(window.location.search).get("mode") === "webview"
  }, [])

  const hideNavigation = isExternalPage || isWebView

  return (
    <ThemeProvider theme={muiTheme}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <>
          {!hideNavigation && (
            <IconButton
              icon="menu-line"
              size="md"
              type="ghost"
              className={classNames("open-btn", { isDrawerOpen })}
              onClick={() => setIsDrawerOpen(!isDrawerOpen)}
            />
          )}
          <div
            className={classNames("Container", { external: isExternalPage, webview: isWebView })}>
            <Snackbar {...snackBar} onClose={handleSnackbarClose} />
            {!isWebView && noticeData && (
              <LatestNoticeDialog
                {...noticeData}
                open={noticeDialogOpen}
                onClose={() => setNoticeDialogOpen(false)}
              />
            )}
            {!hideNavigation && (
              <SideDrawer
                isDrawerOpen={isDrawerOpen}
                isMobile={isMobile}
                onClose={() => setIsDrawerOpen(false)}
              />
            )}
            {isExternalPage ? (
              children
            ) : (
              <div className="BodyContainer">
                {!isWebView && <AppBar isDrawerOpen={isDrawerOpen} isMobile={isMobile} />}
                <div className={classNames(isLegacyReport ? "iframe-body" : "Body")}>
                  {!isWebView && <BreadCrumbs isAtIframe={isLegacyReport} />}
                  {isHome || isLegacyReport ? (
                    children
                  ) : (
                    <div className="BodyContentCont">{children}</div>
                  )}
                </div>
                {isHome && !isWebView && <Footer />}
              </div>
            )}
          </div>
        </>
      </LocalizationProvider>
    </ThemeProvider>
  )
}
